import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { Router } from '@angular/router';

import { AppError, AppErrorCodes } from '@core/api';
import { IntercomService } from '@core/providers';

import { ErrorStateCardComponent } from '../../components/error-state-card/error-state-card.component';

@Component({
  selector: 'la-app-page-error',
  templateUrl: './app-page-error.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ErrorStateCardComponent],
})
export class AppPageErrorComponent {
  @Input() error?: AppError;
  @Output() recover = new EventEmitter<void>();

  public readonly CODES = AppErrorCodes;
  private readonly router = inject(Router);
  private readonly intercom = inject(IntercomService);

  get title(): string {
    if (this.error?.status === 404) {
      return 'Page not found';
    }

    if (this.error?.status && this.error.status >= 500) {
      return 'Something went wrong';
    }

    if (this.error?.status === 429) {
      return 'Too many requests';
    }

    switch (this.error?.code) {
      case this.CODES.NO_INTERNET:
        return 'No internet';
      case this.CODES.DEPRECATED:
        return 'New version available';
      case this.CODES.NO_MAP_PERMISSIONS:
      case this.CODES.NO_PERMISSIONS:
        return 'No permission';
      default:
        return 'Error';
    }
  }

  get body(): string {
    if (this.error?.status === 404) {
      return "Let's get you back on the map.";
    }

    if (this.error?.status && this.error.status >= 500) {
      return "Sorry, we're having trouble loading this page. Please try again in a few minutes or contact us for support if the problem continues.";
    }

    if (this.error?.status === 429) {
      return "We're receiving too many requests at the moment. Please try again in a few minutes.";
    }
    switch (this.error?.code) {
      case this.CODES.NO_INTERNET:
        return "You're not connected to the internet at the moment, so any changes you make won't be saved. Please check your connection and try again.";
      case this.CODES.DEPRECATED:
        return 'Please refresh your browser or clear your cache to ensure you are using the latest version of the Land App.';
      case this.CODES.NO_MAP_PERMISSIONS:
        return "You don't have permission to see this map yet. Please ask the map owner to share the map with you or your team.\nIn the meantime, you can continue mapping on other maps.";
      case this.CODES.NO_PERMISSIONS:
        return "Let's get you back on the map.";
      default:
        return this.error?.message ?? 'Unknown error';
    }
  }

  get showGoToMaps(): boolean {
    return (
      this.error?.status === 404 || this.error?.code === this.CODES.NO_MAP_PERMISSIONS || this.error?.code === this.CODES.NO_PERMISSIONS
    );
  }

  handleGoToMaps() {
    this.intercom.hide();
    this.router.navigate(['/maps']).then(() => {
      this.recover.emit();
    });
  }
}
